<template>
  <div>
    <h1 class="headline">Whom do you appoint as your proxy holder?</h1>
    <p class="gq-text__secondary" v-if="snippet"><span v-html="snippet" /></p>
    <p class="gq-text__secondary" v-else-if="!experimentalMode">
      This person will attend the meeting and cast your vote on your behalf.
    </p>
    <p v-else>
      Your proxy holder will attend the meeting and cast your vote on your
      behalf. Enter their name below:
    </p>

    <div v-if="!experimentalMode">
      <div class="toggle-list mdl-shadow--2dp">
        <toggle-list-item
          v-for="(candidate, index) in holdersList"
          :key="index"
          :value="candidate"
          icon="account_circle"
          toggle-icon="check"
          :text="candidate"
          :checked-status="candidate === proxyHolder.name"
          toggle-class="toggle-content-action"
          @inputting="setDefaultHolder($event)"
        />

        <toggle-list-item
          value="other"
          icon="create"
          toggle-icon="check"
          text="I'd like to choose someone else"
          :checked-status="isOther"
          toggle-class="toggle-content-action"
          @inputting="clickOther"
        />
      </div>

      <!-- allow Writing in own candidate -->
      <div class="toggle-other-box" v-show="isOther">
        <mdl-textfield
          field-id="addProxyHolder"
          :label="labelText"
          :placeholder="placeholderText"
          @inputting="setCustomProxyHolder"
          :bvalue="proxyHolder.name"
          @go-next-input="goInput('buttonProxyNext')"
        ></mdl-textfield>
      </div>
    </div>

    <!-- Experimental Mode Proxy Interface-->
    <div v-else>
      <div class="horizontal-display chip-list">
        <proxy-holder-chip-list
          :proxy-holders="otherProxyHolders"
          :selected-proxy="proxyHolder.name"
          :proxy-display-options="proxyDisplayOptions"
          @inputting="setCustomProxyHolder"
          @go-next="goInput('buttonProxyNext')"
        ></proxy-holder-chip-list>
      </div>

      <div
        v-if="showDropdown"
        class="mdl-textfield mdl-js-textfield mdl-textfield--floating-label horizontal-display"
      >
        <label class="mdl-textfield__label lbl" for="custom-dropdown">
          {{ dropdownLabel }}
        </label>

        <select
          class="mdl-textfield__input"
          id="custom-dropdown"
          name="custom-dropdown"
          @change="updateDropdownSelection($event)"
          :required="true"
        >
          <option v-for="option in dropdownOptions" v-bind:key="option.id">
            {{ option }}
          </option>
        </select>
      </div>
    </div>
    <!-- End Experimental Mode -->
    <div v-if="showCheckbox" class="checkbox">
      <label
        id="agree"
        class="mdl-checkbox mdl-js-checkbox mdl-js-ripple-effect"
        for="acceptFallback"
      >
        <input
          type="checkbox"
          id="acceptFallback"
          class="mdl-checkbox__input"
          @change="handleCheckbox"
        />
        <span v-if="checkboxText" class="mdl-checkbox__label checkbox-label">
          {{ checkboxText }}
        </span>
        <span v-else class="mdl-checkbox__label checkbox-label">
          Should my designated proxy holder fail to attend the meeting, I agree
          that {{ fallbackHoldersText }} shall be appointed as my fallback proxy
          holder.
        </span>
      </label>
    </div>

    <div class="App-buttons">
      <button-component direction="back" value="Back"></button-component>
      <button-component
        field-id="buttonProxyNext"
        color="primary"
        value="Next Step"
        :is-disabled="isDisabled"
      ></button-component>
    </div>

    <frequently-asked-questions
      :is-fallback-holder-disabled="isFallbackHolderDisabled"
      :fallback-holders-text="fallbackHoldersText"
      :is-ontario-proxy="isBill106"
    />
  </div>
</template>

<script>
import _ from 'lodash';

import ButtonComponent from '@/components/Button.vue';
import mdlTextfield from '@/components/mdlTextfield.vue';
import ProxyHolderChipList from '@/components/ProxyHolder/ProxyHolderChipList.vue';
import ToggleListItem from '@/components/Questions/ToggleListItem.vue';
import FrequentlyAskedQuestions from '@/components/FrequentlyAskedQuestions.vue';

import {mapGetters, mapActions} from 'vuex';

// import api from '@/api.js';

export default {
  components: {
    ButtonComponent,
    mdlTextfield,
    ProxyHolderChipList,
    ToggleListItem,
    FrequentlyAskedQuestions
  },
  computed: {
    ...mapGetters({
      meeting: 'meeting/meeting',
      proxyHolder: 'proxy/proxyHolder',
      displayOptions: 'displayOptions',
      meetingOptions: 'meetingOptions'
    }),
    snippet() {
      return _.get(this.displayOptions, 'proxy.snippet', null);
    },
    labelText() {
      const def = "Enter your proxy holder's name below:"; // eslint-disable-line
      const label = _.get(this.displayOptions, 'proxy.write_in_label', '');
      return label === '' ? def : label;
    },
    placeholderText() {
      const def = 'eg. John Smith';
      const placeholder = _.get(
        this.displayOptions,
        'proxy.write_in_placeholder',
        ''
      );
      return placeholder === '' ? def : placeholder;
    },
    isDisabled() {
      return (
        !this.proxyHolder.name ||
        this.proxyHolder.name === 'other' ||
        (this.showCheckbox && !this.acceptFallback)
      );
    },
    isDefault() {
      return (
        (this.proxyHolder.isDefault && this.proxyHolder.isTouched) ||
        this.holdersList.includes(this.proxyHolder.name)
      );
    },
    isOther() {
      return !this.proxyHolder.isDefault && this.proxyHolder.isTouched;
    },
    otherProxyHolders() {
      // Get optional proxies and filter empty values
      const otherProxies = _.get(
        this.meeting.options,
        'other_proxy_holders',
        []
      ).filter(v => v);

      // Combine the default and optional proxy list
      return [...this.meeting.defaultHolders, ...otherProxies];
    },
    listAllDefaultHolders() {
      return _.get(
        this.displayOptions,
        'proxy.list_all_default_holders',
        false
      );
    },
    holdersList() {
      // Check if we want to show all default holders or only the first one
      return this.listAllDefaultHolders
        ? this.meeting.defaultHolders
        : [this.meeting.defaultHolders[0]];
    },
    experimentalMode() {
      return _.get(this.displayOptions, 'proxy.experimental_mode', false);
    },
    proxyDisplayOptions() {
      return _.get(this.displayOptions, 'proxy', {});
    },
    fallbackHoldersText() {
      const list = this.meeting.defaultHolders;

      // Lists longer than two should out put as "A, B, or C"
      if (list.length > 2) {
        return `${list.slice(0, -1).join(', ')} or ${list.slice(-1)}`;
      } else if (list.length >= 1) {
        return `${list.join(' or ')}`;
      } else {
        return `${this.list[0]}`;
      }
    },
    showDropdown() {
      return _.get(this.displayOptions, 'proxy.display_custom_dropdown', false);
    },
    dropdownLabel() {
      return _.get(
        this.displayOptions,
        'proxy.custom_proxy_dropdown.label',
        ''
      );
    },
    dropdownOptions() {
      // we check if showDropdown is enabled to ensure that if any options
      // are specified in the Settings page BUT the dropdown shouldn't be used,
      // we don't append the default selection to proxy holder's name by accident
      if (this.showDropdown && this.experimentalMode) {
        const options = _.get(
          this.displayOptions,
          'proxy.custom_proxy_dropdown.options',
          []
        ).filter(v => v);

        return options;
      } else {
        return null;
      }
    },
    showCheckbox() {
      return (
        !this.meeting.defaultHolders.includes(this.proxyHolder.name) &&
        !this.isFallbackHolderDisabled
      );
    },
    checkboxText() {
      return _.get(this.displayOptions, 'proxy.write_in_checkbox', '');
    },
    isFallbackHolderDisabled() {
      return _.get(this.displayOptions, 'proxy.disable_fallback_holder', false);
    },
    isBill106() {
      const template = _.get(this.meetingOptions, 'proxy_template', '');
      return template === 'on-bill-106';
    }
  },
  data() {
    return {
      acceptFallback: false
    };
  },
  mounted() {
    // if dropdown exists, make sure initial selection is set
    if (this.dropdownOptions) {
      this.setProxyDropdownSelection(this.dropdownOptions[0]);
    }
  },
  methods: {
    ...mapActions('proxy', [
      'setProxyHolder',
      'setOtherProxyHolder',
      'setProxyDropdownSelection'
    ]),
    clickDefault() {
      const proxyHolder = {isTouched: true, isDefault: true, name: 'default'};
      this.setProxyHolder(proxyHolder);
    },
    clickOther() {
      const proxyHolder = {isTouched: true, isDefault: false, name: ''};
      this.setProxyHolder(proxyHolder);
    },
    setDefaultHolder(candidate) {
      const proxyHolder = {isTouched: true, isDefault: true, name: candidate};
      this.setProxyHolder(proxyHolder);
    },
    goInput(val) {
      this.$events.$emit('go-input', val);
    },
    setCustomProxyHolder(value) {
      this.setOtherProxyHolder(value);
    },
    updateDropdownSelection(event) {
      this.setProxyDropdownSelection(event.target.value);
    },
    handleCheckbox() {
      this.acceptFallback = !this.acceptFallback;
    }
  }
};
</script>

<style scoped>
.horizontal-display {
  display: inline-block;
  margin-right: 40px;
}

.mdl-textfield__input {
  cursor: pointer;
  background-color: #ffffff;
  border-radius: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  margin-top: 10px;
  height: 39px;
}

.mdl-card {
  min-height: 0;
}

.icon {
  color: #3f51b5;
  opacity: 0.8;
  display: flex;
  align-items: center;
}

.info-icon {
  font-size: 3rem;
}

.chip-list {
  margin-bottom: 10px;
}

.checkbox {
  margin-top: 10px;
}

.mdl-checkbox {
  height: auto;
}

.mdl-checkbox__label {
  font-size: 14px;
}
</style>
